/**
 * Tutor reassignment modal.
 * Coordinator with `canDirectAssign` writes a validated assignment; otherwise the establishment is submitted for coordinator validation.
 */
import { useState, useMemo } from 'react';
import { Building2, MapPin } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Select } from '@/components/forms/Select';
import { DepartmentSelect } from '@/components/common/DepartmentSelect';
import {
  useReassignEstablishment,
  useCoordinatorDirectAssign,
  useEligibleEstablishments,
} from '@/hooks/useUsers';
import { TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER } from '@/components/tutors/tutor-configs';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel, getRoleLabelPlural } from '@/utils/role-label-utils';
import { toast } from '@/components/ui/toast';

/** Props for {@link ReassignmentModal}. */
interface ReassignmentModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly userId: string;
  readonly companyId: string;
  readonly defaultDepartments: string[];
  readonly canDirectAssign: boolean;
  readonly onSuccess?: () => void;
}

/** Establishment {@link Select} placeholder based on department / loading / empty list. */
function establishmentPlaceholder(
  selectedDepartment: string,
  isLoadingEstablishments: boolean,
  hasOptions: boolean,
): string {
  if (!selectedDepartment) return TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER.noDepartment;
  if (isLoadingEstablishments) return TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER.loading;
  if (!hasOptions) return TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER.empty;
  return TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER.select;
}

/** Tutor reassignment modal. */
export function ReassignmentModal({
  open,
  onClose,
  userId,
  companyId,
  defaultDepartments,
  canDirectAssign,
  onSuccess,
}: ReassignmentModalProps) {
  // Single department selector (pre-filled with first tutor department, full list available)
  const [selectedDepartment, setSelectedDepartment] = useState<string>(defaultDepartments[0] ?? '');
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');

  /** API hook expects a department list; the UI only allows one at a time. */
  const deptArray = selectedDepartment ? [selectedDepartment] : [];

  const { data: eligibleEstablishments, isLoading: isLoadingEstablishments } =
    useEligibleEstablishments(deptArray, companyId, { enabled: deptArray.length > 0 });

  const reassignMutation = useReassignEstablishment();
  const directAssignMutation = useCoordinatorDirectAssign();

  const isPending = reassignMutation.isPending || directAssignMutation.isPending;

  /** Eligible establishments for the selected department (prospection validated). */
  const establishmentOptions = useMemo(() => {
    if (!eligibleEstablishments) return [];
    return eligibleEstablishments.map((estab: any) => ({
      value: estab.id,
      label: estab.name || estab.id,
    }));
  }, [eligibleEstablishments]);

  /** Changing department drops the establishment so we never keep an outdated id. */
  const handleDepartmentChange = (code: string) => {
    setSelectedDepartment(code);
    setSelectedEstablishmentId('');
  };

  /** Reset local state so the next open starts from the tutor defaults. */
  const handleClose = () => {
    onClose();
    setSelectedDepartment(defaultDepartments[0] ?? '');
    setSelectedEstablishmentId('');
  };

  /** Direct-assign (validated) vs reassign (awaiting coordinator), then close. */
  const handleSubmit = async () => {
    if (!selectedEstablishmentId) return;

    try {
      if (canDirectAssign) {
        await directAssignMutation.mutateAsync({
          userId,
          companyId,
          establishmentId: selectedEstablishmentId,
        });
      } else {
        await reassignMutation.mutateAsync({
          userId,
          companyId,
          establishmentId: selectedEstablishmentId,
        });
      }
      handleClose();
      onSuccess?.();
    } catch (error: unknown) {
      toast.error(getErrorMessage(error));
    }
  };

  const canSubmit = !!selectedEstablishmentId && !isPending;

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="lg"
      header={{
        icon: Building2,
        iconSize: 'md',
        iconColor: 'rgb(var(--color-2-nuance-1))',
        iconBgColor: 'color-mix(in srgb, rgb(var(--color-2-nuance-1)) 15%, transparent)',
        title: 'Réaffecter le tuteur',
        description: "Sélectionnez le département et l'établissement d'affectation pour ce tuteur.",
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      footer={
        <div className="flex gap-2 justify-end w-full">
          <Button preset="modal-cancel" onClick={handleClose} disabled={isPending}>
            Annuler
          </Button>
          <Button
            preset="modal-confirm"
            onClick={handleSubmit}
            loading={isPending}
            disabled={!canSubmit}
          >
            Valider
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">
              Département(s) d'affectation
            </span>
          </div>
          <DepartmentSelect
            label=""
            value={selectedDepartment}
            onChange={handleDepartmentChange}
            placeholder="Sélectionner un département"
          />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Établissement d'affectation</span>
          </div>
          <Select
            label=""
            placeholder={establishmentPlaceholder(
              selectedDepartment,
              isLoadingEstablishments,
              establishmentOptions.length > 0,
            )}
            value={selectedEstablishmentId}
            onChange={setSelectedEstablishmentId}
            options={establishmentOptions}
            disabled={isPending || !selectedDepartment || isLoadingEstablishments}
          />
          <p className="text-xs text-muted-foreground">
            Seuls les établissements au statut de prospection "Validé" sont proposés.
          </p>
        </div>

        <div
          className="rounded-lg border p-3 text-xs"
          style={{
            borderColor: 'color-mix(in srgb, rgb(var(--color-2-nuance-1)) 30%, transparent)',
            background: 'color-mix(in srgb, rgb(var(--color-2-nuance-1)) 6%, transparent)',
          }}
        >
          <p className="font-medium" style={{ color: 'var(--color-1-nuance-1)' }}>
            Après validation
          </p>
          <p className="mt-1 text-muted-foreground">
            {canDirectAssign
              ? `L'établissement sélectionné deviendra l'affectation définitive du ${getRoleLabel('tutor').toLowerCase()}. Le ${getRoleLabel('tutor').toLowerCase()} passera au statut "Actif" et apparaîtra dans la vue "${getRoleLabelPlural('tutor')}".`
              : `L'établissement proposé sera soumis pour validation par le ${getRoleLabel('coordinator').toLowerCase()}. Le ${getRoleLabel('tutor').toLowerCase()} apparaîtra dans la vue "À valider".`}
          </p>
        </div>
      </div>
    </BaseModal>
  );
}
